package fr.manyverse.app.presentation.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.util.toDisplayNameFromFileName
import fr.manyverse.app.presentation.components.ImagePickerButton
import fr.manyverse.app.presentation.components.ValidatedTextField
import fr.manyverse.app.presentation.components.rememberImageSelection
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.CharacterViewModel
import fr.manyverse.app.presentation.viewmodel.UniverseSelectionViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.back
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.character_name
import manyverse.shared.generated.resources.character_name_hint
import manyverse.shared.generated.resources.character_story
import manyverse.shared.generated.resources.character_story_hint
import manyverse.shared.generated.resources.create
import manyverse.shared.generated.resources.create_character
import manyverse.shared.generated.resources.field_required
import manyverse.shared.generated.resources.image_optional
import manyverse.shared.generated.resources.select_universe
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CreateCharacterScreen(
    userId: String,
    onNavigateBack: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: CharacterViewModel = koinViewModel(),
    universeViewModel: UniverseSelectionViewModel = koinViewModel()
) {
    var name by remember { mutableStateOf("") }
    var story by remember { mutableStateOf("") }
    val imageSelection = rememberImageSelection { image ->
        if (name.isBlank()) name = image.fileName.toDisplayNameFromFileName()
    }
    var selectedUniverseId by remember { mutableStateOf<String?>(null) }
    var universeDropdownExpanded by remember { mutableStateOf(false) }

    val universesState by universeViewModel.uiState.collectAsStateWithLifecycle()
    val universes = when (val state = universesState) {
        is AsyncUiState.Success -> state.data
        else -> emptyList()
    }

    LaunchedEffect(userId) {
        universeViewModel.loadUniverses(userId)
    }

    val createState by viewModel.createCharacterState.collectAsStateWithLifecycle()

    LaunchedEffect(createState) {
        if (createState is AsyncUiState.Success) {
            viewModel.resetCreateCharacterState()
            onNavigateBack()
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(stringResource(Res.string.create_character)) },
                navigationIcon = {
                    IconButton(
                        onClick = onNavigateBack,
                        modifier = Modifier.testTag("backButton")
                    ) {
                        Icon(
                            Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = stringResource(Res.string.back)
                        )
                    }
                }
            )
        },
        modifier = modifier
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .imePadding()
                .verticalScroll(rememberScrollState())
                .padding(16.dp)
                .safeContentPadding(),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            // Image picker
            Box(
                modifier = Modifier.fillMaxWidth(),
                contentAlignment = Alignment.Center
            ) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    ImagePickerButton(
                        currentImageUrl = null,
                        selectedImageData = imageSelection.selected,
                        onPickImage = imageSelection::pick,
                        onRemoveImage = imageSelection::clear,
                        enabled = createState !is AsyncUiState.Loading,
                        modifier = Modifier.testTag("imagePickerButton")
                    )
                    Text(
                        text = stringResource(Res.string.image_optional),
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }

            ValidatedTextField(
                value = name,
                onValueChange = { name = it },
                label = stringResource(Res.string.character_name),
                placeholder = stringResource(Res.string.character_name_hint),
                helperText = if (name.isBlank()) stringResource(Res.string.field_required) else null,
                enabled = createState !is AsyncUiState.Loading,
                keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                modifier = Modifier.testTag("nameField")
            )

            // Universe selector (required)
            ExposedDropdownMenuBox(
                expanded = universeDropdownExpanded,
                onExpandedChange = { universeDropdownExpanded = it },
                modifier = Modifier.fillMaxWidth().testTag("universeDropdown")
            ) {
                val selectedUniverse = universes.firstOrNull { it.id == selectedUniverseId }
                OutlinedTextField(
                    value = selectedUniverse?.displayName ?: "",
                    onValueChange = {},
                    readOnly = true,
                    label = { Text(stringResource(Res.string.select_universe)) },
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = universeDropdownExpanded) },
                    modifier = Modifier
                        .fillMaxWidth()
                        .menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable),
                    enabled = createState !is AsyncUiState.Loading
                )
                ExposedDropdownMenu(
                    expanded = universeDropdownExpanded,
                    onDismissRequest = { universeDropdownExpanded = false }
                ) {
                    universes.forEach { universe ->
                        DropdownMenuItem(
                            text = { Text(universe.displayName) },
                            onClick = {
                                selectedUniverseId = universe.id
                                universeDropdownExpanded = false
                            },
                            modifier = Modifier.testTag("universeOption_${universe.id}")
                        )
                    }
                }
            }

            OutlinedTextField(
                value = story,
                onValueChange = { story = it },
                label = { Text(stringResource(Res.string.character_story)) },
                placeholder = { Text(stringResource(Res.string.character_story_hint)) },
                minLines = 5,
                maxLines = 10,
                enabled = createState !is AsyncUiState.Loading,
                keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag("storyField")
            )

            if (createState is AsyncUiState.Error) {
                Card(
                    colors = CardDefaults.cardColors(
                        containerColor = MaterialTheme.colorScheme.errorContainer
                    ),
                    modifier = Modifier
                        .fillMaxWidth()
                        .testTag("errorCard")
                ) {
                    Text(
                        text = (createState as AsyncUiState.Error).message,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onErrorContainer,
                        modifier = Modifier
                            .padding(16.dp)
                            .testTag("errorMessage")
                    )
                }
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                OutlinedButton(
                    onClick = onNavigateBack,
                    enabled = createState !is AsyncUiState.Loading,
                    modifier = Modifier
                        .weight(1f)
                        .testTag("cancelButton")
                ) {
                    Text(stringResource(Res.string.cancel))
                }

                Button(
                    onClick = {
                        val universeId = selectedUniverseId ?: return@Button
                        viewModel.createCharacter(
                            universeId = universeId,
                            userId = userId,
                            name = name,
                            story = story,
                            imageData = imageSelection.selected
                        )
                    },
                    enabled = name.isNotBlank() &&
                            selectedUniverseId != null &&
                            createState !is AsyncUiState.Loading,
                    modifier = Modifier
                        .weight(1f)
                        .testTag("createButton")
                ) {
                    if (createState is AsyncUiState.Loading) {
                        CircularProgressIndicator(
                            modifier = Modifier
                                .size(24.dp)
                                .testTag("loadingIndicator"),
                            color = MaterialTheme.colorScheme.onPrimary,
                            strokeWidth = 2.dp
                        )
                    } else {
                        Text(stringResource(Res.string.create))
                    }
                }
            }
        }
    }
}
